import React, { useState, useEffect, useRef } from 'react';
import { Heart, ShoppingBag, X, Trash2, Sparkles, Share2, Award, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { getImageUrl, getWishlist, removeFromWishlist, addToCart } from '../apiConfig';
import './WishlistPage.css';

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const mainRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchWishlistData();

    const ctx = gsap.context(() => {
      gsap.from('.wishlist-hero-content h1', { y: 50, opacity: 0, duration: 1.2, ease: 'power4.out' });
      gsap.from('.wishlist-hero-content p', { y: 30, opacity: 0, duration: 1.2, delay: 0.3, ease: 'power4.out' });
      
      // Background Glows Animation
      gsap.to('.glow-1', { x: '15%', y: '10%', duration: 8, ease: 'sine.inOut' });
      gsap.to('.glow-2', { x: '-15%', y: '-10%', duration: 10, ease: 'sine.inOut', delay: 1 });
    });
    return () => ctx.revert();
  }, []);

  const fetchWishlistData = async () => {
    setLoading(true);
    try {
      const apiWishlist = await getWishlist();
      setWishlistItems(apiWishlist);
      localStorage.setItem('wishlistItems', JSON.stringify(apiWishlist));
    } catch (err) {
      console.error("Wishlist Load Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (id) => {
    const updated = wishlistItems.filter(item => item.id !== id && item._id !== id);
    setWishlistItems(updated);
    localStorage.setItem('wishlistItems', JSON.stringify(updated));
    window.dispatchEvent(new Event('wishlistUpdated'));

    const token = localStorage.getItem('token');
    if (token) {
      try {
        await removeFromWishlist(id);
      } catch (err) {
        console.error("Failed to remove from wishlist API:", err);
      }
    }
  };

  const clearAll = () => {
    setWishlistItems([]);
    localStorage.setItem('wishlistItems', JSON.stringify([]));
    window.dispatchEvent(new Event('wishlistUpdated'));
  };

  const moveAllToBag = async () => {

    setLoading(true);
    try {
      // Loop through all wishlist items and add them to the cart
      for (const item of wishlistItems) {
        const productId = item.id || item._id;
        const variantId = item.variants?.[0]?._id || item.variants?.[0]?.id || null;
        if (productId) {
          await addToCart(productId, variantId, 1);
        }
      }

      // Clear wishlist since they are all moved to bag!
      setWishlistItems([]);
      localStorage.setItem('wishlistItems', JSON.stringify([]));
      window.dispatchEvent(new Event('wishlistUpdated'));
      
      // Best-effort cleanup on API
      await Promise.all(wishlistItems.map(async (item) => {
        const id = item._id || item.id;
        try {
          await removeFromWishlist(id);
        } catch (e) {
          console.warn("Failed to remove item from wishlist on backend:", id, e);
        }
      }));
    } catch (err) {
      console.error("Error moving collection to bag:", err);
    } finally {
      setLoading(false);
    }
  };

  const parsePrice = (price) => {
    if (!price) return 0;
    if (typeof price === 'number') return price;
    const numericPart = String(price).replace(/[^0-9]/g, '');
    return parseInt(numericPart) || 0;
  };

  const calculateTotal = () => {
    return wishlistItems.reduce((acc, item) => {
      const priceVal = item.variants?.[0]?.price || item.price;
      return acc + parsePrice(priceVal);
    }, 0);
  };

  return (
    <div className="wishlist-page-v2 glass-theme" ref={mainRef}>
      {/* ── BACKGROUND ACCENTS ── */}
      <div className="bg-glows">
        <div className="glow glow-1"></div>
        <div className="glow glow-2"></div>
        <div className="glow glow-3"></div>
      </div>

      {/* ── HERO SECTION ── */}
      <section className="wishlist-hero-v2">
        <div className="wishlist-hero-overlay-v2" />
        <div className="container relative-z">
          <div className="wishlist-hero-content">
            <div className="editorial-badge">
              <Sparkles size={14} color="#d4af37" />
              <span>PRIVATE COLLECTION</span>
            </div>
            <h1>My Luxury <span className="gold-shimmer-text">Archive</span></h1>
            <p className="hero-subtitle">A curated selection of your most desired olfactory masterpieces.</p>
            
            <div className="hero-actions-v2">
              <button className="share-btn-v2">
                <Share2 size={16} /> SHARE ARCHIVE
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <div className="wishlist-main-wrapper-v2">
        <div className="container">
          {loading ? (
            <div className="wishlist-loading-wrap">
              <div className="luxury-spinner" />
              <p>Authenticating your collection...</p>
            </div>
          ) : wishlistItems.length > 0 ? (
            <div className="wishlist-layout-v2">

              {/* Items List */}
              <div className="wishlist-items-container">
                <div className="wishlist-header-v2">
                  <div className="h-info">
                    <h3>CURATED ITEMS</h3>
                    <span>{wishlistItems.length} Fragrances Found</span>
                  </div>
                  <button className="clear-archive-btn" onClick={clearAll}>
                    <Trash2 size={14} />
                    <span>CLEAR ARCHIVE</span>
                  </button>
                </div>

                <div className="wishlist-list-v2">
                  {wishlistItems.map((item, idx) => (
                    <div key={item._id || idx} className="wish-card-v2 reveal-up">
                      <div className="wish-card-img-wrap">
                        <img
                          src={getImageUrl(item.images?.[0])}
                          alt={item.name}
                          className="wish-img"
                        />
                        <div className="wish-img-overlay">
                          <button className="remove-btn-v2" onClick={() => removeItem(item._id)}>
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="wish-card-content">
                        <div className="wish-card-main">
                          <div className="card-top">
                            <span className="item-brand">{item.brand || 'ESCENTRUM PARIS'}</span>
                            <div className="match-tag">
                              <Award size={12} /> 98% MATCH
                            </div>
                          </div>
                          <h4 className="item-name">{item.name}</h4>
                          <div className="item-meta">
                            <span>{item.fragranceFamily || 'Oriental'}</span>
                            <div className="dot-sep"></div>
                            <span>{item.variants?.[0]?.size || '100ml'}</span>
                          </div>
                        </div>

                        <div className="wish-card-footer">
                          <div className="item-price-v2">
                            <span className="price-label">EST. VALUE</span>
                            <span className="price-val">PKR {parsePrice(item.variants?.[0]?.price || item.price).toLocaleString()}</span>
                          </div>
                          <button className="add-bag-btn-v2" onClick={() => navigate(`/product/${item._id || item.id}`)}>
                            <span>VIEW DETAILS</span>
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Section */}
              <div className="wishlist-sidebar-v2">
                <div className="summary-glass-card">
                  <div className="summary-head">
                    <Clock size={20} color="#d4af37" />
                    <h4>ARCHIVE SUMMARY</h4>
                  </div>
                  
                  <div className="summary-list">
                    <div className="s-row">
                      <span>Collection Count</span>
                      <span>{wishlistItems.length} Items</span>
                    </div>
                    <div className="s-row">
                      <span>Average Concentration</span>
                      <span>Extrait de Parfum</span>
                    </div>
                    <div className="s-row highlight">
                      <span>Total Valuation</span>
                      <span className="total-price">PKR {calculateTotal().toLocaleString()}</span>
                    </div>
                  </div>

                  <button className="checkout-all-btn" onClick={moveAllToBag}>
                    <ShoppingBag size={18} />
                    MOVE COLLECTION TO BAG
                  </button>
                  
                  <div className="summary-footer">
                    <p>Prices include luxury tax and insurance. White-glove delivery available at checkout.</p>
                  </div>
                </div>
                
                {/* Concierge Card */}
                <div className="concierge-mini-card">
                  <Sparkles size={24} color="#d4af37" />
                  <h5>Scent Concierge</h5>
                  <p>Need help deciding? Our master curators are available for a private fitting.</p>
                  <button onClick={() => navigate('/contact')}>CONSULT NOW</button>
                </div>
              </div>

            </div>
          ) : (
            <div className="wishlist-empty-v2">
              <div className="empty-visual">
                <div className="empty-ring"></div>
                <Heart size={80} strokeWidth={0.5} className="empty-heart" />
              </div>
              <h2>Your Archive is Empty</h2>
              <p>The journey to your signature scent begins with a single selection.</p>
              <button className="master-explore-btn" onClick={() => navigate('/shop')}>
                EXPLORE THE COLLECTIONS
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WishlistPage;
